import React, { useEffect} from "react";
import { Link, useParams } from "react-router-dom";
import Carousel from "react-bootstrap/Carousel";
import { Image } from "react-bootstrap";
import { toast } from "react-hot-toast";
import Loader from "../../components/Loader";

// Breadcrumb
import Breadcrumb from "../../components/layout/Breadcrumb/Breadcrumb-Productdetails";

// BtnAddToCart
import BtnAddToCart from "../../components/layout/Buttons/BtnAddToCart";

// Redux API
import { useGetProductDetailsQuery } from "../../redux/api/productsApi";

// ProductAttributes
import ProductAttributes from "./ProductAttributes";

import MetaData from "../../components/layout/MetaData";

const ProductDetails = () => {
  const params = useParams();
  const { data, isLoading, error, isError } = useGetProductDetailsQuery(params?.id);
  const product = data?.product;


  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message);
    }
  }, [isError, error?.data?.message]);
  if (isLoading) return <Loader />;

  return (
    <>
      <MetaData title={product?.name} />
      <main className="px-4 lg:px-24 bg-[#F5F9FF] py-8">
        <div className="hidden md:block">
          <Breadcrumb product={product} />
        </div>
        <article className="flex flex-col lg:flex-row w-full gap-8 mt-4 md:mt-8">
          {/* Left Column: Product Identity and Pricing */}
          <section className="w-full lg:w-[350px] flex flex-col gap-6">
            {/* Top Section: Identity, Image & Add to Cart */}
            <div className="flex flex-col items-center">
              <div className="text-center mb-6">
                <h5 className="text-gray-400 text-[10px] md:text-xs font-medium uppercase tracking-wider mb-1">{product?.name}</h5>
                <h1 className="text-lg md:text-xl font-bold text-gray-800 mb-2">{product?.name}</h1>
                <div className="text-gray-900 font-bold text-sm md:text-base">
                  {product?.stock > 0 ? `${product?.stock} In Stock` : "Out Of Stock"}
                </div>
              </div>
              
              <div className="w-full mb-6 flex justify-center py-4 px-2">
                <Image
                  src={product?.image[0] || "/img/image_not_available.png"}
                  className="max-w-full h-auto object-contain max-h-[160px] md:max-h-[200px]"
                  alt={product?.name}
                />
              </div>

              <div className="w-full pt-4">
                <BtnAddToCart {...product} />
              </div>
            </div>

            {/* Bottom Section: Bulk Pricing */}
            <div className="mt-8">
              <div className="mb-4">
                <h2 className="font-bold text-lg md:text-2xl text-gray-800">Bulk Pricing</h2>
              </div>
              <div className="w-full h-[1px] bg-gray-200 mb-6"></div>
              <div className="overflow-x-auto bg-white rounded-lg shadow-sm">
                <table className="w-full text-xs md:text-sm text-center border-collapse">
                  <thead className="bg-white">
                    <tr>
                      <th className="px-2 md:px-4 py-4 text-[#3B82F6] font-bold">Qty</th>
                      <th className="px-2 md:px-4 py-4 text-[#3B82F6] font-bold border-l border-[#D7E6FF]">Unit Price</th>
                      <th className="px-2 md:px-4 py-4 text-[#3B82F6] font-bold border-l border-[#D7E6FF]">Ext Price</th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#F5F9FF]">
                    {product?.bulk_Pricing?.map((price, i) => {
                      const qty = parseInt(price.Qty);
                      // 处理价格字符串，移除所有非数字和非小数点字符
                      const unitPriceStr = price?.UnitPrice?.toString().replace(/[^0-9.]/g, '') || "0";
                      const unitPrice = parseFloat(unitPriceStr);
                      const extPrice = (qty * unitPrice);

                      return (
                        <tr key={i} className="hover:bg-[#EAF2FF] transition-colors">
                          <td className="px-2 md:px-4 py-4 text-gray-600">{qty}</td>
                          <td className="px-2 md:px-4 py-4 text-gray-600 border-l border-[#D7E6FF]">
                            {isNaN(unitPrice) ? "0.00000" : unitPrice.toFixed(5)}
                          </td>
                          <td className="px-2 md:px-4 py-4 text-gray-600 border-l border-[#D7E6FF]">
                            {isNaN(extPrice) ? "$NaN" : `$${extPrice.toFixed(3)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
          
          {/* Right Column: Product Specifications */}
          <section className="flex-1 bg-white border border-gray-100 p-6 md:p-10 rounded-lg shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)]">
            <div className="mb-10">
              <h2 className="font-bold text-lg md:text-2xl text-gray-900 mb-6 md:mb-8">
                {product?.name}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 md:gap-y-4">
                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">CTYEA Part Number</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.CTYEA_Part_Number}</span>
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Manufacturer Part Number</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.Manufacturer_Part_Number}</span>
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Manufacturer</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.Manufacturer}</span>
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Description</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.description}</span>
                </div>

                <div className="flex items-center py-2 md:border-b md:border-gray-50 pr-4 mt-2 md:mt-0">
                  <span className="text-gray-500 font-medium text-xs md:text-sm">Weight</span>
                </div>
                <div className="flex items-center py-1 md:py-2 border-b border-gray-50">
                  <span className="text-gray-800 text-sm">{product?.Weight || "-"}</span>
                </div>
              </div>
            </div>

            <ProductAttributes attributes={product?.product_Attributes} />
          </section>
        </article>
      </main>
    </>
  );
};

export default ProductDetails;
